import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type {
  CommunityChannelCreate,
  CommunityChannelUpdate,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";

export function useCommunityChannels(communityId: string) {
  const client = useQueryClient();
  const { user, loading } = useAuth();
  const enabled = Boolean(communityId && user?.uid) && !loading;
  const queryKey = [...queryKeys.social.communityChannels(communityId), user?.uid ?? null];
  const query = useQuery({
    queryKey,
    queryFn: () => communityApi.listChannels(communityId),
    enabled,
    refetchOnMount: "always",
    staleTime: 0,
    refetchInterval: 15_000,
    retry: false,
  });
  const channels = {
    ...query,
    data: enabled && query.isFetchedAfterMount && !query.isError ? query.data : undefined,
  };
  const refresh = async () => {
    await client.cancelQueries({ queryKey, exact: true });
    await client.invalidateQueries({ queryKey });
  };
  const create = useMutation({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (data: CommunityChannelCreate) =>
      communityApi.createChannel(communityId, data),
    onSuccess: refresh,
    onError: refresh,
  });
  const update = useMutation({
    mutationFn: ({
      conversationId,
      data,
    }: {
      conversationId: string;
      data: CommunityChannelUpdate;
    }) => communityApi.updateChannel(conversationId, data),
    onSuccess: refresh,
    onError: refresh,
  });
  const archive = useMutation({
    mutationFn: (conversationId: string) =>
      communityApi.archiveChannel(conversationId),
    onSuccess: refresh,
    onError: refresh,
  });
  return { channels, create, update, archive };
}
